import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft,
  Mail,
  Phone,
  Euro,
  Calendar,
  Briefcase,
  Home as HomeIcon,
  Loader2,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContatti = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchRequests();
  }, [navigate]);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/contact-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data.requests || [];
      setRequests(data);
      
      // Calculate stats
      setStats({
        total: data.length,
        new: data.filter(r => r.status === 'new').length,
        contacted: data.filter(r => r.status === 'contacted').length,
        closed: data.filter(r => r.status === 'closed').length
      });
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getJobTypeLabel = (type) => {
    const labels = {
      dipendente: 'Dipendente',
      studente: 'Studente',
      autonomo: 'Autonomo'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Richieste di Contatto</h1>
              <p className="text-slate-600">Gestisci le richieste dai visitatori</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <p className="text-blue-100 text-sm mb-1">Totale Richieste</p>
            <p className="text-3xl font-bold">{stats.total}</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <p className="text-emerald-100 text-sm mb-1">Nuove</p>
            <p className="text-3xl font-bold">{stats.new}</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
            <p className="text-yellow-100 text-sm mb-1">In Corso</p>
            <p className="text-3xl font-bold">{stats.contacted}</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-slate-500 to-slate-600 text-white">
            <p className="text-slate-100 text-sm mb-1">Chiuse</p>
            <p className="text-3xl font-bold">{stats.closed}</p>
          </Card>
        </div>

        {/* Requests List */}
        {requests.length === 0 ? (
          <Card className="p-12 text-center">
            <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Nessuna richiesta ancora
            </h3>
            <p className="text-slate-600">
              Le richieste di contatto dai visitatori appariranno qui
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex gap-6">
                  {/* Property Image */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 bg-slate-100 rounded-lg overflow-hidden">
                      {request.property_image ? (
                        <img
                          src={`${BACKEND_URL}/api/property-image/${request.property_id}/${request.property_image}`}
                          alt={request.property_title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <HomeIcon className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 mb-1">
                          {request.property_title}
                        </h3>
                        <p className="text-sm text-slate-500">
                          Richiesta del {formatDate(request.created_at)}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        request.status === 'new' 
                          ? 'bg-emerald-100 text-emerald-700'
                          : request.status === 'contacted'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {request.status === 'new' ? 'Nuova' : request.status === 'contacted' ? 'In Corso' : 'Chiusa'}
                      </span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Nome</p>
                          <p className="font-semibold text-slate-900">{request.name}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Phone className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Telefono</p>
                          <a href={`tel:${request.phone}`} className="font-semibold text-emerald-600 hover:underline">
                            {request.phone}
                          </a>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Lavoro</p>
                          <p className="font-semibold text-slate-900">{getJobTypeLabel(request.job_type)}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Euro className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Budget</p>
                          <p className="font-semibold text-slate-900">€{request.monthly_budget}/mese</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Disponibile da</p>
                          <p className="font-semibold text-slate-900">{request.available_from}</p>
                        </div>
                      </div>
                    </div>

                    {request.message && (
                      <div className="pt-4 border-t border-slate-200">
                        <p className="text-xs text-slate-500 mb-2">Messaggio</p>
                        <p className="text-sm text-slate-700">{request.message}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContatti;
